import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tag,
  Typography,
  Popconfirm,
  InputNumber,
  DatePicker
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { HomeOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import '../MemberManagement.css';

const { Text, Paragraph } = Typography;

type TeamOpt = { _id: string; name: string; code?: string };
type AuthTeamRow = {
  teamId: string;
  team?: TeamOpt;
  expiresAt?: string;
  authorizedAt?: string;
};
type MerchantRow = {
  _id: string;
  merchantCode?: string;
  name: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: string;
  status: string;
  reviewStatus?: string;
  reviewNote?: string;
  principalName?: string;
  principalIdNumber?: string;
  businessLicenseUrl?: string;
  commitmentLetterUrl?: string;
  pendingProfile?: Record<string, string> | null;
  userCount?: number;
  authorizedTeams?: AuthTeamRow[];
  authorizedTeamIds?: TeamOpt[];
};

type AuthDraft = { teamId: string; expiresAt: Dayjs | null };

async function saFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/superadmin${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {})
    }
  });
  return res.json();
}

const SuperadminMerchants: React.FC = () => {
  const [merchants, setMerchants] = useState<MerchantRow[]>([]);
  const [teams, setTeams] = useState<TeamOpt[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState<MerchantRow | null>(null);
  const [authDrafts, setAuthDrafts] = useState<AuthDraft[]>([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState('');
  const [reviewOpen, setReviewOpen] = useState<MerchantRow | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [form] = Form.useForm();
  const [inviteForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, tRes] = await Promise.all([saFetch('/merchants'), saFetch('/teams')]);
      if (mRes.success) setMerchants(mRes.data || []);
      if (tRes.success) {
        setTeams(
          (tRes.data || []).map((t: TeamOpt) => ({
            _id: t._id,
            name: t.name,
            code: t.code
          }))
        );
      }
    } catch {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createMerchant = async () => {
    const values = await form.validateFields();
    const body = await saFetch('/merchants', { method: 'POST', body: JSON.stringify(values) });
    if (!body.success) {
      message.error(body.error || '创建失败');
      return;
    }
    message.success('商家已创建');
    setCreateOpen(false);
    form.resetFields();
    void load();
  };

  const openAuth = (row: MerchantRow) => {
    setAuthOpen(row);
    const existing = row.authorizedTeams || [];
    if (existing.length) {
      setAuthDrafts(
        existing.map((a) => ({
          teamId: String(a.teamId || a.team?._id || ''),
          expiresAt: a.expiresAt ? dayjs(a.expiresAt) : dayjs().add(1, 'year')
        }))
      );
    } else {
      setAuthDrafts([]);
    }
  };

  const saveAuth = async () => {
    if (!authOpen) return;
    if (authDrafts.some((d) => !d.teamId)) {
      message.error('请为每一行选择团队');
      return;
    }
    if (authDrafts.some((d) => !d.expiresAt)) {
      message.error('请为每个授权团队设置到期时间');
      return;
    }
    const ids = authDrafts.map((d) => d.teamId);
    if (new Set(ids).size !== ids.length) {
      message.error('同一团队不能重复授权');
      return;
    }

    const body = await saFetch(`/merchants/${authOpen._id}/authorized-teams`, {
      method: 'PUT',
      body: JSON.stringify({
        authorizations: authDrafts.map((d) => ({
          teamId: d.teamId,
          expiresAt: d.expiresAt!.endOf('day').toISOString()
        }))
      })
    });
    if (!body.success) {
      message.error(body.error || '保存失败');
      return;
    }
    message.success('授权已更新');
    setAuthOpen(null);
    void load();
  };

  const createInvite = async () => {
    const values = await inviteForm.validateFields();
    const body = await saFetch('/merchant-invites', {
      method: 'POST',
      body: JSON.stringify(values)
    });
    if (!body.success) {
      message.error(body.error || '生成失败');
      return;
    }
    const path = body.data?.registerPath || '';
    const full = `${window.location.origin}${path}`;
    setInviteResult(full);
    message.success('注册链接已生成');
    void load();
  };

  const toggleStatus = async (row: MerchantRow) => {
    const next = row.status === 'active' ? 'disabled' : 'active';
    const body = await saFetch(`/merchants/${row._id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: next })
    });
    if (!body.success) {
      message.error(body.error || '操作失败');
      return;
    }
    message.success(next === 'active' ? '已启用' : '已停用');
    void load();
  };

  const submitReview = async (decision: 'approve' | 'reject') => {
    if (!reviewOpen) return;
    setReviewLoading(true);
    try {
      const body = await saFetch(`/merchants/${reviewOpen._id}/review`, {
        method: 'POST',
        body: JSON.stringify({ decision, note: reviewNote })
      });
      if (!body.success) {
        message.error(body.error || '审核失败');
        return;
      }
      message.success(decision === 'approve' ? '已通过审核' : '已驳回');
      setReviewOpen(null);
      setReviewNote('');
      void load();
    } finally {
      setReviewLoading(false);
    }
  };

  const resolveDocUrl = (url?: string) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/uploads/')) return `/api${url}`;
    return url;
  };

  const reviewTag = (status?: string) => {
    const map: Record<string, { color: string; text: string }> = {
      pending: { color: 'orange', text: '待入驻审核' },
      approved: { color: 'green', text: '已通过' },
      rejected: { color: 'red', text: '已驳回' },
      pending_rereview: { color: 'gold', text: '变更待复审' }
    };
    const m = map[status || 'approved'] || { color: 'default', text: status || '—' };
    return <Tag color={m.color}>{m.text}</Tag>;
  };

  const contactCell = (row: MerchantRow) => {
    const phone = row.contactPhone?.trim();
    const email = row.contactEmail?.trim();
    if (!phone && !email) return <Text type="secondary">—</Text>;
    return (
      <div style={{ lineHeight: 1.45 }}>
        {phone ? <div>{phone}</div> : null}
        {email ? (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {email}
            </Text>
          </div>
        ) : null}
      </div>
    );
  };

  const columns: ColumnsType<MerchantRow> = [
    { title: '商家编号', dataIndex: 'merchantCode', width: 140, render: (v) => v || '—' },
    { title: '商家名称', dataIndex: 'name', width: 140 },
    { title: '联系人', dataIndex: 'contactName', width: 100, render: (v) => v || '—' },
    {
      title: '联系方式',
      key: 'contact',
      width: 180,
      render: (_, row) => contactCell(row)
    },
    { title: '账号数', dataIndex: 'userCount', width: 80 },
    {
      title: '审核',
      width: 120,
      render: (_, row) => reviewTag(row.reviewStatus)
    },
    {
      title: '状态',
      width: 90,
      render: (_, row) =>
        row.status === 'active' ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>
    },
    {
      title: '已授权团队',
      width: 260,
      render: (_, row) => {
        const list = row.authorizedTeams || [];
        if (!list.length) return <Text type="secondary">未授权</Text>;
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {list.map((a) => {
              const name = a.team?.name || a.teamId;
              const exp = a.expiresAt ? dayjs(a.expiresAt).format('YYYY-MM-DD') : '—';
              const soon =
                a.expiresAt && dayjs(a.expiresAt).diff(dayjs(), 'day') <= 14;
              return (
                <div key={a.teamId}>
                  <Text>{name}</Text>
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    至 {exp}
                  </Text>
                  {soon ? (
                    <Tag color="orange" style={{ marginLeft: 6 }}>
                      即将到期
                    </Tag>
                  ) : null}
                </div>
              );
            })}
          </Space>
        );
      }
    },
    {
      title: '操作',
      width: 340,
      fixed: 'right',
      render: (_, row) => (
        <Space wrap>
          <Button
            size="small"
            type={
              row.reviewStatus === 'pending' || row.reviewStatus === 'pending_rereview'
                ? 'primary'
                : 'default'
            }
            onClick={() => {
              setReviewOpen(row);
              setReviewNote('');
            }}
          >
            资料审核
          </Button>
          <Button size="small" onClick={() => openAuth(row)}>
            团队授权
          </Button>
          <Button
            size="small"
            type="primary"
            ghost
            onClick={() => {
              setInviteOpen(true);
              setInviteResult('');
              inviteForm.setFieldsValue({
                merchantId: row._id,
                merchantNameHint: row.name,
                expiresInDays: 7,
                maxUses: 1
              });
            }}
          >
            注册链接
          </Button>
          <Popconfirm
            title={row.status === 'active' ? '确认停用该商家？' : '确认启用？'}
            onConfirm={() => void toggleStatus(row)}
          >
            <Button size="small" danger={row.status === 'active'}>
              {row.status === 'active' ? '停用' : '启用'}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const selectedTeamIds = new Set(authDrafts.map((d) => d.teamId).filter(Boolean));

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>平台管理</span>
        <span className="mm-crumb-sep">/</span>
        <span>商家管理</span>
      </nav>

      <header className="mm-page-head d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h1 className="mm-title">商家管理</h1>
          <p className="mm-subtitle mb-0">
            创建商家、生成注册链接、按团队授权可见范围（授权到期后自动失效）
          </p>
        </div>
        <Space wrap>
          <Button
            onClick={() => {
              setInviteOpen(true);
              setInviteResult('');
              inviteForm.resetFields();
              inviteForm.setFieldsValue({ expiresInDays: 7, maxUses: 1 });
            }}
          >
            生成空白注册链接
          </Button>
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建商家
          </Button>
        </Space>
      </header>

      <div className="content-area px-1 px-md-2">
        <div className="am-table-shell bg-white rounded-3 border p-2 p-md-3">
          <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={merchants}
            scroll={{ x: 1100 }}
          />
        </div>
      </div>

      <Modal
        title="新建商家"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void createMerchant()}
        okText="创建"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="商家名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contactName" label="联系人">
            <Input />
          </Form.Item>
          <Form.Item name="contactPhone" label="电话">
            <Input />
          </Form.Item>
          <Form.Item name="contactEmail" label="邮箱">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`团队授权 · ${authOpen?.name || ''}`}
        open={!!authOpen}
        onCancel={() => setAuthOpen(null)}
        onOk={() => void saveAuth()}
        okText="保存"
        width={640}
        destroyOnHidden
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          仅在授权有效期内的团队，发起「商家申请」时能看到该商家；到期后系统会自动解除授权。
        </Paragraph>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {authDrafts.map((draft, idx) => (
            <Space key={idx} wrap align="start" style={{ width: '100%' }}>
              <Select
                style={{ width: 260 }}
                placeholder="选择团队"
                value={draft.teamId || undefined}
                onChange={(teamId) => {
                  setAuthDrafts((prev) =>
                    prev.map((d, i) => (i === idx ? { ...d, teamId } : d))
                  );
                }}
                options={teams.map((t) => ({
                  value: t._id,
                  label: `${t.name}${t.code ? ` (${t.code})` : ''}`,
                  disabled: selectedTeamIds.has(t._id) && t._id !== draft.teamId
                }))}
              />
              <DatePicker
                style={{ width: 160 }}
                placeholder="授权到期日"
                value={draft.expiresAt}
                disabledDate={(current) => !!current && current.endOf('day').isBefore(dayjs())}
                onChange={(v) => {
                  setAuthDrafts((prev) =>
                    prev.map((d, i) => (i === idx ? { ...d, expiresAt: v } : d))
                  );
                }}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => setAuthDrafts((prev) => prev.filter((_, i) => i !== idx))}
              />
            </Space>
          ))}
          <Button
            type="dashed"
            block
            onClick={() =>
              setAuthDrafts((prev) => [
                ...prev,
                { teamId: '', expiresAt: dayjs().add(1, 'year') }
              ])
            }
          >
            添加授权团队
          </Button>
        </Space>
      </Modal>

      <Modal
        title="生成商家注册链接"
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onOk={() => void createInvite()}
        okText="生成"
      >
        <Form form={inviteForm} layout="vertical">
          <Form.Item name="merchantId" label="关联已有商家（可选）">
            <Select
              allowClear
              placeholder="不选则注册时创建新商家"
              options={merchants.map((m) => ({ value: m._id, label: m.name }))}
            />
          </Form.Item>
          <Form.Item name="merchantNameHint" label="商家名称提示">
            <Input placeholder="新商家预填名称" />
          </Form.Item>
          <Form.Item name="expiresInDays" label="有效天数" initialValue={7}>
            <InputNumber min={1} max={90} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="maxUses" label="最大使用次数" initialValue={1}>
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
        {inviteResult && (
          <Paragraph copyable style={{ marginTop: 12 }}>
            {inviteResult}
          </Paragraph>
        )}
      </Modal>

      <Modal
        title={`资料审核 · ${reviewOpen?.name || ''}`}
        open={!!reviewOpen}
        onCancel={() => setReviewOpen(null)}
        width={720}
        footer={
          <Space>
            <Button onClick={() => setReviewOpen(null)}>关闭</Button>
            <Button danger loading={reviewLoading} onClick={() => void submitReview('reject')}>
              驳回
            </Button>
            <Button type="primary" loading={reviewLoading} onClick={() => void submitReview('approve')}>
              通过审核
            </Button>
          </Space>
        }
      >
        {reviewOpen && (
          <>
            <Paragraph>
              当前审核状态：{reviewTag(reviewOpen.reviewStatus)}
              {reviewOpen.reviewNote ? (
                <Text type="secondary">（上次备注：{reviewOpen.reviewNote}）</Text>
              ) : null}
            </Paragraph>
            {(() => {
              const src =
                reviewOpen.reviewStatus === 'pending_rereview' && reviewOpen.pendingProfile
                  ? { ...reviewOpen, ...reviewOpen.pendingProfile }
                  : reviewOpen;
              return (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {reviewOpen.reviewStatus === 'pending_rereview' && (
                    <Paragraph type="warning">以下为商家提交的待复审变更资料</Paragraph>
                  )}
                  <div>负责人：{src.principalName || '—'} / {src.principalIdNumber || '—'}</div>
                  <div>联系人：{src.contactName || '—'} · {src.contactPhone || '—'} · {src.contactEmail || '—'}</div>
                  <div>地址：{src.address || '—'}</div>
                  <div>
                    营业执照：{' '}
                    {src.businessLicenseUrl ? (
                      <a href={resolveDocUrl(src.businessLicenseUrl)} target="_blank" rel="noreferrer">
                        查看文件
                      </a>
                    ) : (
                      '未上传'
                    )}
                  </div>
                  <div>
                    承诺书：{' '}
                    {src.commitmentLetterUrl ? (
                      <a href={resolveDocUrl(src.commitmentLetterUrl)} target="_blank" rel="noreferrer">
                        查看文件
                      </a>
                    ) : (
                      '未上传'
                    )}
                  </div>
                </Space>
              );
            })()}
            <Form.Item label="审核备注" style={{ marginTop: 16, marginBottom: 0 }}>
              <Input.TextArea
                rows={3}
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="驳回时建议填写原因，商家端可见"
              />
            </Form.Item>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SuperadminMerchants;
